import { useState } from 'react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    projet: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d506pst0fc9f5lc3prfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          projet: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white p-5 sm:p-8 lg:p-10 rounded-2xl shadow-xl border border-gray-100">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
        Demande de <span className="text-blue-600">devis</span>
      </h2>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.</p>
      
      <form onSubmit={handleSubmit} data-readdy-form id="contact-page-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div>
            <label htmlFor="nom" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
              Nom complet *
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300"
              placeholder="Votre nom"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300"
              placeholder="votre@email.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div>
            <label htmlFor="telephone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300"
              placeholder="+243 973 452 626"
            />
          </div>
          <div>
            <label htmlFor="projet" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
              Type de projet *
            </label>
            <select
              id="projet"
              name="projet"
              value={formData.projet}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 transition-all duration-300"
            >
              <option value="">Sélectionnez un type</option>
              <option value="construction-neuve">Construction neuve</option>
              <option value="renovation">Rénovation</option>
              <option value="extension">Extension</option>
              <option value="amenagement">Aménagement</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            Description du projet *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            maxLength={500}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none transition-all duration-300"
            placeholder="Décrivez votre projet en détail..."
          ></textarea>
          <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 caractères</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 sm:py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
        >
          {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
        </button>

        {submitStatus === 'success' && (
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-start text-sm sm:text-base">
            <i className="ri-checkbox-circle-line text-lg sm:text-xl mr-2 sm:mr-3 mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="font-semibold">Message envoyé avec succès !</p>
              <p className="text-xs sm:text-sm mt-1">Nous vous contacterons rapidement.</p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start text-sm sm:text-base">
            <i className="ri-error-warning-line text-lg sm:text-xl mr-2 sm:mr-3 mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="font-semibold">Une erreur s'est produite</p>
              <p className="text-xs sm:text-sm mt-1">Veuillez réessayer ou nous contacter directement.</p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
